import {FC, MouseEvent, useState} from 'react';
import {useCanAny, usePersona} from 'src/permissions/hooks';
import {CreditCardOutlined} from '@mui/icons-material';
import {Box, Popover} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetIcons} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneFact} from 'src/components/mobile/PhoneCardFacts/PhoneCardFacts.types';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import theme from 'src/theme';
import {pillShapePhoneSx} from 'src/components/common/pillFit.sx';
import {rfidItemCardSx} from './RfidItemCardForDedicatedPage.sx';
import {RfIdItemCardType} from './RfidItemCardForDedicatedPage.types';
import {rfidTableSx} from '../RfidTable.sx';
import dayjs from 'dayjs';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';
import {BlockStatusEnum} from '../../RfidTable/RfidTable.types';
import {RfidActionsForDedicatedPage} from '../../RfidTable/RfidActionsForDedicatedPage';
import {tagOwnerLabelId, tagOwnerOf} from 'src/utils/tagOwner';

export const RfidItemCardForDedicatedPage: FC<RfIdItemCardType> = ({tag, businessId, holderView = false}) => {
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);


    const persona = usePersona();
    // The platform administrator reads the same card as the business does: within one business the
    // two are looking at the same tag, and the administrator's own variant left out who holds it.
    const isBusinessUser = persona.isBackOffice;
    const isCompanyUser = persona.isCompany;
    const isEmployeeUser = persona.isEmployee;
    // The menu opens RfidActionsForDedicatedPage - edit, block, delete. It used to be hidden from
    // companies and employees by persona; a company holds all three of those rights, so the card was
    // withholding a menu its owner was entitled to.
    const canManageRfid = useCanAny('rfidTags.update', 'rfidTags.delete');
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Whose card this is, read the one way every view of a tag reads it: the kind is the holder's
    // account type, not their roles.
    const owner = tagOwnerOf(tag);

    const employeeName = tag?.employee?.firstName && tag?.employee?.lastName
        ? `${tag.employee.firstName} ${tag.employee.lastName}`
        : '';

    const licensePlate = tag?.licensePlate || '—';
    const addedOn = tag?.created ? dayjs(tag.created).format('DD/MM/YYYY') : '—';

    /**
     * The way into the tag's own page.
     *
     * The desktop column has always linked the tag number; the card never did, so below `md` the
     * page simply had no door - the list was the end of the road. Same destination, written the same
     * way as the column beside it so the two cannot drift apart.
     */
    const tagTitle = (
        <Box
            component={Link}
            to={`/app/rfids/${tag?.id}`}
            sx={{color: 'inherit', textDecoration: 'none'}}
        >
            {tag?.idTag ?? ''}
        </Box>
    );

    const statusPill = (
        <Box
            sx={[
                rfidTableSx.tag,
                !tag.blocked && rfidTableSx.tagActive,
                tag.blocked && rfidTableSx.tagBlocked,
            ]}
        >
            <FormattedMessage id={tag.blocked ? BlockStatusEnum.blocked : BlockStatusEnum.accepted}/>
        </Box>
    );

    // The same four facts the holder's table shows; the actions are in the strip above the list.
    if (holderView) {
        return (
            <TableCard>
                <PhoneCardHead
                    icon={<CreditCardOutlined/>}
                    title={tagTitle}
                />
                <Box sx={phonePillsRowSx}>
                    <Box
                        sx={[
                            rfidTableSx.tag,
                            !tag.blocked && rfidTableSx.tagActive,
                            tag.blocked && rfidTableSx.tagBlocked,
                        ]}
                    >
                        <FormattedMessage id={tag.blocked ? 'rfidDeactivated' : 'active'}/>
                    </Box>
                </Box>
                <PhoneCardFacts
                    facts={[
                        {label: <FormattedMessage id="licensePlate"/>, value: licensePlate},
                        {label: <FormattedMessage id="rfidTagAddedOnTableHeader"/>, value: addedOn},
                    ]}
                />
            </TableCard>
        );
    }

    // One node, drawn by whichever of the two the width calls for: every handler and every
    // permission gate inside it is the same in both.
    const tagActions = (
        <RfidActionsForDedicatedPage
            businessId={businessId ?? tag?.business?.id}
            blocked={tag?.blocked}
            ocppTagId={tag?.id}
            idTabObject={tag}
        />
    );

    // The card's one menu, in the head rather than absolutely placed over the corner of the card.
    const menu = (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<CreditCardOutlined/>}
                    title={tag?.idTag}
                    subtitle={tag?.licensePlate || undefined}
                >
                    <ActionSheetIcons>{tagActions}</ActionSheetIcons>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={rfidItemCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {tagActions}
                </Popover>
            )}
        </>
    );

    // Each reader keeps the columns their own table gave them: a company and an employee are shown
    // the employee the card was handed to and when it was issued, the business the kind of holder and
    // whose it is. A persona that matched neither was shown the tag alone, and still is.
    const showsCompanyView = isCompanyUser || isEmployeeUser;
    const facts: PhoneFact[] = [];

    if (showsCompanyView) {
        facts.push(
            {label: <FormattedMessage id="licensePlate"/>, value: licensePlate},
            {label: <FormattedMessage id="rfidTagAddedOnTableHeader"/>, value: addedOn}
        );
    } else if (isBusinessUser) {
        facts.push(
            {label: <FormattedMessage id="employee"/>, value: employeeName || '—'},
            {label: <FormattedMessage id="licensePlate"/>, value: licensePlate}
        );
    }

    const subtitle = showsCompanyView
        ? employeeName || undefined
        : (isBusinessUser ? tag?.company?.name || undefined : undefined);

    return (
        <TableCard>
            <PhoneCardHead
                icon={<CreditCardOutlined/>}
                title={tagTitle}
                subtitle={subtitle}
                action={canManageRfid ? menu : undefined}
            />
            {(showsCompanyView || isBusinessUser) && (
                <Box sx={phonePillsRowSx}>
                    {/* Whose card it is. The caption goes through `tagOwnerLabelId`, which is the
                        whole point of that utility: a holder's account type is a stored value -
                        PRIVATE_DRIVER - and rendering it raw put "PRIVATE DRIVER" on the card
                        where the rest of the product says "Private driver". */}
                    {isBusinessUser && owner?.type && (
                        <Box
                            sx={[
                                rfidTableSx.tag,
                                pillShapePhoneSx,
                                {
                                    color: theme.palette.neutral[700],
                                    backgroundColor: theme.palette.neutral[100],
                                },
                            ]}
                        >
                            <FormattedMessage id={tagOwnerLabelId(owner.type)}/>
                        </Box>
                    )}
                    {statusPill}
                </Box>
            )}
            <PhoneCardFacts facts={facts}/>
        </TableCard>
    );
};
